import React from 'react';
import { View } from 'react-native';
import {
  Box,
  Inline,
  IBoxProps,
} from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import StripeCardInput from '../StripeCardInput/StripeCardInput';
import { Icon, BodyText } from '../../../ui';
import verifiedAndSecuredIcon from '../../../../../images/icon-lock-shield.png';
import StripeCardIcons from './StripeCardIcons';
import './StripeCardField.module.scss';
import type { StripeCardChangeEvent, StripeElement } from '../types';
import type { StripeElementProps } from '../StripeElementProps';

export type StripeCardFieldProps = {
  onComplete?: (eventValue: StripeElement) => void;
  onChange?: (eventValue: StripeElement) => void;
  onError?: (eventValue: string) => void;
  onFocus?: StripeElementProps['onFocus'];
  backgroundColor?: IBoxProps['color'];
  borderColor: IBoxProps['borderColor'];
  borderWidth: IBoxProps['borderWidth'];
  borderRadius?: IBoxProps['borderRadius'];
  hideTrustIcons?: boolean;
  hideErrors?: boolean;
  /** Horizontal padding around entire component */
  paddingX?: IBoxProps['paddingX'];
  /** Vertical padding above the component */
  paddingTop?: IBoxProps['paddingTop'];
  paddingBottom?: IBoxProps['paddingBottom'];
  style?: IBoxProps['style'];
};

/**
 * Adds an UI around the stripe card field
 * showing supported card banks and the "secured transaction" message
 * Will need the stripe context
 * @returns {*}
 * @constructor
 *
 * @example
 * import { withStripeContext } from '../../shared/stripe/StripeContext';
 * import StripeCardField from '../../shared/stripe/StripeCardField/StripeCardField';
 * const MyComponent = () => {
 *  return (
 *    <StripeCardField
 *      ...
 *    />
 *  )
 * }
 *
 * export default withStripeContext(MyComponent)
 */

export const StripeCardField = ({
  onComplete = () => {},
  onChange = () => {},
  onFocus = () => {},
  onError = () => {},
  backgroundColor = BrandColor.LightGrey,
  borderColor = null,
  borderWidth = null,
  hideTrustIcons = false,
  hideErrors = false,
  paddingX = 12,
  paddingTop = 16,
  paddingBottom = undefined,
  borderRadius = 4,
  style,
}: StripeCardFieldProps) => {
  const [inputErrors, setInputErrors] = React.useState(null);

  const handleCreditCardComplete = (eventValue: StripeElement) => {
    setInputErrors(null);
    onComplete(eventValue);
  };

  const handleCreditCardChange = (
    eventValue: StripeElement,
    event: StripeCardChangeEvent,
  ) => {
    if (!event.error) {
      setInputErrors(null);
    }
    onChange(eventValue);
  };

  const handleCreditCardError = (errors: string) => {
    onError(errors);
    setInputErrors(errors);
  };

  return (
    <Box
      testID="stripe-card-field"
      minWidth="100%"
      paddingX={paddingX}
      paddingTop={paddingTop}
      color={backgroundColor}
      borderColor={borderColor}
      paddingBottom={paddingBottom}
      borderRadius={borderRadius}
    >
      {(inputErrors && !hideErrors) && (
        <View styleName="error-container">
          <BodyText
            appearance="normal"
            styleName="errors"
            testID="stripe-card-error"
          >
            {inputErrors}
          </BodyText>
        </View>
      )}

      <Box
        minWidth="100%"
        borderColor={borderColor}
        borderWidth={borderWidth}
        borderStyle="solid"
        paddingY={12}
        color={BrandColor.WhiteWash}
        style={style}
      >
        <StripeCardInput
          onComplete={handleCreditCardComplete}
          onChange={handleCreditCardChange}
          onFocus={onFocus}
          onError={handleCreditCardError}
          autoFocus
        />
      </Box>

      {!hideTrustIcons && (
        <Box
          minWidth="100%"
          paddingTop={12}
          flexDirection="row"
          justifyContent="space-between"
        >
          <StripeCardIcons />

          <Inline space={4}>
            <Icon size={16} uri={verifiedAndSecuredIcon} />
            <BodyText styleName="verified-secured-message" appearance="legal">
              Verified &amp; Secured
            </BodyText>
          </Inline>
        </Box>
      )}
    </Box>
  );
};

export default StripeCardField;
